import {
  Box,
  Button,
  Switch as ChakraSwitch,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Select,
  SimpleGrid,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, FieldError, SubmitHandler, useForm } from 'react-hook-form'
import { RiQuestionLine } from 'react-icons/ri'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'

import { Input } from '../../../components/Form/Input'
import { PaymentConditionPeriodicityOptions } from '../../../Interfaces/enums'
import { PaymentCondition } from '../../../Interfaces/paymentCondition'
import { api } from '../../../services/api'

type FormValues = {
  name: string
  description?: string
  installments: number
  day_to_payment: number
  is_active: boolean
  discount_percentage?: number
  interest_percentage?: number
  recurrence: boolean
  periodicity: 'DIARIO' | 'SEMANAL' | 'MENSAL' | 'ANUAL'
  billing_day?: number
}

const schema = yup.object().shape({
  name: yup.string().required('Nome é obrigatório'),
  description: yup.string().nullable(),
  installments: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? 1 : value))
    .min(1, 'Informe pelo menos 1 parcela')
    .required('Número de parcelas é obrigatório'),
  day_to_payment: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? 0 : value))
    .min(0, 'Dia para pagamento não pode ser negativo')
    .required('Dia para pagamento é obrigatório'),
  is_active: yup.boolean().default(true),
  discount_percentage: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? 0 : value))
    .min(0, 'O desconto não pode ser negativo'),
  interest_percentage: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? 0 : value))
    .min(0, 'O juros não pode ser negativo'),
  recurrence: yup.boolean().default(false),
  periodicity: yup
    .string()
    .oneOf(['DIARIO', 'SEMANAL', 'MENSAL', 'ANUAL'], 'Periodicidade inválida')
    .when('recurrence', {
      is: true,
      then: (schema) => schema.required('Periodicidade é obrigatória quando recorrência está ativa'),
      otherwise: (schema) => schema.optional(),
    }),
  billing_day: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .min(1, 'O dia de cobrança deve ser entre 1 e 31')
    .max(31, 'O dia de cobrança deve ser entre 1 e 31')
    .when('recurrence', {
      is: true,
      then: (schema) => schema.required('Dia de cobrança é obrigatório quando recorrência está ativa'),
      otherwise: (schema) => schema.nullable(),
    }),
})

export default function PaymentConditionForm({ match }: any) {
  const history = useHistory()
  const toast = useToast()
  const queryClient = useQueryClient()

  const { id } = match.params
  const isEditMode = Boolean(id)

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      is_active: true,
      installments: 1,
      day_to_payment: 0,
      recurrence: false,
      periodicity: 'MENSAL' as const,
      billing_day: undefined,
    },
  })

  useQuery(
    ['payment-condition', id],
    async () => {
      const { data } = await api.get(`payment-conditions/${id}`)
      return data?.data as PaymentCondition
    },
    {
      enabled: isEditMode,
      onSuccess: (paymentCondition) => {
        if (!paymentCondition) return

        reset({
          name: paymentCondition.name,
          description: paymentCondition.description || '',
          installments: paymentCondition.installments,
          day_to_payment: paymentCondition.day_to_payment,
          is_active: paymentCondition.is_active,
          discount_percentage: paymentCondition.discount_percentage,
          interest_percentage: paymentCondition.interest_percentage,
          recurrence: paymentCondition.recurrence || false,
          periodicity: paymentCondition.periodicity || 'MENSAL',
          billing_day: paymentCondition.billing_day,
        })
      },
    },
  )

  const mutation = useMutation(
    async (values: FormValues) => {
      const payload = {
        ...values,
        description: values.description?.trim() || undefined,
        installments: Number(values.installments) || 1,
        day_to_payment: Number(values.day_to_payment) || 0,
        discount_percentage: Number(values.discount_percentage) || 0,
        interest_percentage: Number(values.interest_percentage) || 0,
        recurrence: Boolean(values.recurrence),
        periodicity: values.recurrence ? values.periodicity : undefined,
        billing_day: values.recurrence && values.billing_day ? Number(values.billing_day) : null,
      }

      if (isEditMode) {
        await api.put(`payment-conditions/${id}`, payload)
      } else {
        await api.post('payment-conditions', payload)
      }
    },
    {
      onSuccess: async () => {
        toast({
          description: `Condição de pagamento ${isEditMode ? 'atualizada' : 'criada'} com sucesso`,
          status: 'success',
        })
        await queryClient.invalidateQueries('payment-conditions')
        history.push('/payment-conditions')
      },
      onError: ({ response }) => {
        const errors = response?.data?.errors || []
        if (errors.length) {
          errors.forEach((err) => toast({ description: err.message, status: 'error' }))
        } else {
          toast({ description: 'Não foi possível salvar a condição de pagamento', status: 'error' })
        }
      },
    },
  )

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    await mutation.mutateAsync(values)
  }

  return (
    <Box as="form" flex="1" borderRadius="8" bg="gray.800" p="8" onSubmit={handleSubmit(onSubmit)}>
      <Heading size="lg" fontWeight="normal">
        {isEditMode ? 'Editar Condição de Pagamento' : 'Nova Condição de Pagamento'}
      </Heading>

      <Divider my="6" borderColor="gray.700" />

      <VStack spacing="8">
        <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
          <Input label="Nome" {...register('name')} error={errors.name as FieldError} />
          <Input label="Descrição" {...register('description')} error={errors.description as FieldError} />
        </SimpleGrid>

        <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
          <Input
            label="Número de parcelas"
            type="number"
            {...register('installments')}
            error={errors.installments as FieldError}
          />
          <FormControl>
            <HStack spacing={2} mb={2}>
              <FormLabel mb={0}>Dia para pagamento</FormLabel>
              <Tooltip
                label="Data em que o título deve ser pago"
                placement="right"
                bg="gray.800"
                color="white"
                fontSize="sm"
                shouldWrapChildren>
                <Icon as={RiQuestionLine} color="gray.400" cursor="help" />
              </Tooltip>
            </HStack>
            <Input type="number" {...register('day_to_payment')} error={errors.day_to_payment as FieldError} />
          </FormControl>
        </SimpleGrid>

        <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
          <Input
            label="Desconto (%)"
            type="number"
            step="0.01"
            {...register('discount_percentage')}
            error={errors.discount_percentage as FieldError}
          />
          <Input
            label="Juros (%)"
            type="number"
            step="0.01"
            {...register('interest_percentage')}
            error={errors.interest_percentage as FieldError}
          />
        </SimpleGrid>

        <Controller
          name="recurrence"
          control={control}
          render={({ field }) => (
            <HStack align="center" spacing={3}>
              <ChakraSwitch
                colorScheme="pink"
                isChecked={!!field.value}
                onChange={(event) => field.onChange(event.target.checked)}
              />
              <Text>Recorrência</Text>
            </HStack>
          )}
        />

        {watch('recurrence') && (
          <>
            <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
              <FormControl>
                <FormLabel>Periodicidade</FormLabel>
                <Select
                  placeholder="Selecione a periodicidade"
                  {...register('periodicity')}
                  bg="gray.700"
                  color="white"
                  borderColor="gray.600"
                  _hover={{ borderColor: 'pink.400' }}
                  _focus={{ borderColor: 'pink.400', boxShadow: 'none', color: 'white' }}
                  sx={{ option: { color: 'black', backgroundColor: 'white' } }}>
                  {PaymentConditionPeriodicityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <HStack spacing={2} mb={2}>
                  <FormLabel mb={0}>Dia de cobrança (1-31)</FormLabel>
                  <Tooltip
                    label="Data em que o sistema gera ou cobra o lançamento recorrente"
                    placement="right"
                    bg="gray.800"
                    color="white"
                    fontSize="sm"
                    shouldWrapChildren>
                    <Icon as={RiQuestionLine} color="gray.400" cursor="help" />
                  </Tooltip>
                </HStack>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  {...register('billing_day')}
                  error={errors.billing_day as FieldError}
                />
              </FormControl>
            </SimpleGrid>
          </>
        )}

        <Controller
          name="is_active"
          control={control}
          render={({ field }) => (
            <HStack align="center" spacing={3}>
              <ChakraSwitch
                colorScheme="pink"
                isChecked={!!field.value}
                onChange={(event) => field.onChange(event.target.checked)}
              />
              <Text>Ativo</Text>
            </HStack>
          )}
        />
      </VStack>

      <Flex mt="8" justify="flex-end">
        <HStack spacing="4">
          <Button variant="ghost" onClick={() => history.push('/payment-conditions')}>
            Cancelar
          </Button>
          <Button type="submit" colorScheme="pink" isLoading={isSubmitting || mutation.isLoading}>
            Salvar
          </Button>
        </HStack>
      </Flex>
    </Box>
  )
}
